import { useEffect, useState } from "react";
import { Button, Divider, Grid, Typography } from "@material-ui/core";
import Api from "lib/api/httpRequestHelper";
import { IsMobile } from "utils/is-mobile";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import InputText from "components/_refactor/text/input";
import UploadFile from "components/_refactor/dropzone";
import ChooseProductDialog from "../components/choose-product-dialog";
import ClearButton from "components/_refactor/button/clear";

const MAX_MEDIA = 4;
const MAX_MEDIA_SIZE = 1024 * 1024; // 1MB

export default function ProductsChoose({
  fieldsData,
  loadingFields,
  isLoading,
  setIsLoading,
  addedProductsList,
  setAddedProductsList,
  clearFilters,
  motivoGeral,
  motivoRequerEvidencia,
}) {
  const [product, setProduct] = useState("");
  const [productsList, setProductsList] = useState([]);
  const [isValidProduct, setIsValidProduct] = useState();
  const [isProductAlreadySelected, setIsProductAlreadySelected] =
    useState(false);
  const [quantity, setQuantity] = useState("");
  const [choosedProduct, setChoosedProduct] = useState();
  const [productMedia, setProductMedia] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [uploadKey, setUploadKey] = useState(0);
  const snackbar = useSnackbar();
  const isMobile = IsMobile();

  const handleSearchProduct = async () => {
    if (!fieldsData?.idPessoa) {
      snackbar.showMessage(
        "Selecione a loja (Razão Social) ou informe um CNPJ válido vinculado à sua conta.",
        { severity: "warning" }
      );
      return;
    }

    setIsLoading(true);

    try {
      const res = await Api.get(
        "product-simple-list",
        "productDigitalApi",
        "?page=" +
          (page === 0 ? page : page - 1) +
          "&pageSize=25&order=D&filter=" +
          product +
          "&idPessoaLoja=" +
          fieldsData.idPessoa +
          "&devolucao=true",
        true
      );

      if (res.Result.TotalRegistros > 1) {
        const pages = Math.ceil(res.Result.TotalRegistros / 25);
        setTotalPages(pages);
        setProductsList(res.Result.ListaProdutos);
        setOpenDialog(true);
      }

      if (res.Result.TotalRegistros === 1) {
        setChoosedProduct(res.Result.ListaProdutos[0]);
        setIsValidProduct(true);
      }

      if (res.Result.TotalRegistros === 0) {
        setIsValidProduct(false);
      }
    } catch (error) {
      snackbar.showMessage(error, {
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateFile = (file) => {
    if (file.size > MAX_MEDIA_SIZE) {
      return {
        code: "file-too-large",
        message: `Tamanho máximo permitido é 1MB. O arquivo ${file.name} tem ${(
          file.size /
          1024 /
          1024
        ).toFixed(2)}MB`,
      };
    }
    return null;
  };

  const handleMediaUpload = (files) => {
    if (!files || files.length === 0) {
      setProductMedia([]);
      return;
    }

    if (files.length > MAX_MEDIA) {
      snackbar.showMessage(`Máximo de ${MAX_MEDIA} arquivos permitido`, {
        severity: "error",
      });
      return;
    }

    const validFiles = files.filter((file) => {
      if (file.size > MAX_MEDIA_SIZE) {
        snackbar.showMessage(
          `Tamanho máximo permitido é 1MB. O arquivo ${file.name} tem ${(
            file.size /
            1024 /
            1024
          ).toFixed(2)}MB`,
          {
            severity: "error",
          }
        );
        return false;
      }
      return true;
    });

    const mediaPromises = validFiles.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const isVideo = file.type.startsWith("video/");
          resolve({
            base64: reader.result,
            name: file.name,
            extension: file.name.split(".").pop().toLowerCase(),
            type: isVideo ? "video" : "image",
            file: file,
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(mediaPromises).then((mediaArray) => {
      setProductMedia(mediaArray);
    });
  };

  const handleAddProduct = () => {
    if (!choosedProduct || !quantity) return;
    if (motivoRequerEvidencia && (!productMedia || productMedia.length === 0)) {
      snackbar.showMessage("Para este motivo é obrigatório informar evidência (imagem ou vídeo).", {
        severity: "error",
      });
      return;
    }

    setAddedProductsList([
      ...addedProductsList,
      {
        idTableProduct: `${choosedProduct.Codigo}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        product: choosedProduct,
        name: choosedProduct.Nome,
        idProduto: choosedProduct.Codigo,
        quantity: quantity,
        images: productMedia,
      },
    ]);

    resetProductForm();
  };

  const resetProductForm = () => {
    setProduct("");
    setChoosedProduct(null);
    setQuantity("");
    setIsValidProduct(undefined);
    setProductMedia([]);
    setUploadKey((k) => k + 1);
  };

  useEffect(() => {
    if (!product) return;
    if (addedProductsList.some((p) => p.Produto === product?.toString())) {
      setIsProductAlreadySelected(true);
      setIsLoading(false);
      return;
    }
    setIsProductAlreadySelected(false);
  }, [product, addedProductsList]);

  useEffect(() => {
    if (choosedProduct) {
      setProduct(`${choosedProduct.Codigo} - ${choosedProduct.Nome}`);
      setIsValidProduct(true);
    }
  }, [choosedProduct]);

  useEffect(() => {
    if (page > 0) {
      handleSearchProduct();
    }
  }, [page]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InputText
            label="Produto(s)"
            disabled={loadingFields.length > 0}
            value={product}
            onChange={(e) => {
              setProduct(e.target.value);
              setIsValidProduct(undefined);
              setIsProductAlreadySelected(false);
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Button
            style={{
              width: isMobile ? "100%" : "17vw",
              marginBottom: isMobile ? "0.75rem" : "0px",
              marginTop: isMobile ? "0.75rem" : "20px",
              height: "38px",
              alignSelf: "center",
              padding: "0",
            }}
            variant="contained"
            onClick={handleSearchProduct}
            disabled={!product || loadingFields.length > 0 || choosedProduct}
          >
            {isLoading ? "Pesquisando..." : "Pesquisar"}
          </Button>
        </Grid>

        <Grid item xs={12} md={4} hidden={!product || !isValidProduct}>
          <InputText
            label="Quantidade"
            disabled={!isValidProduct}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputProps={{ min: 1 }}
            type="number"
          />
        </Grid>

        <Grid item xs={12} hidden={!product || !isValidProduct}>
          <UploadFile
            key={uploadKey}
            multiple={true}
            accept="image/*,video/*"
            message={
              motivoRequerEvidencia
                ? "Selecione imagens ou vídeos ou arraste aqui (máximo 4 arquivos, 1MB cada) * Obrigatório para o motivo da solicitação"
                : "Selecione imagens ou vídeos ou arraste aqui (máximo 4 arquivos, 1MB cada)"
            }
            validator={validateFile}
            onChangeFiles={handleMediaUpload}
            disabled={!isValidProduct || productMedia.length >= MAX_MEDIA}
          />
        </Grid>
      </Grid>

      <Divider variant="fullWidth" style={{ margin: "20px 0px" }} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={4}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleAddProduct}
            disabled={
              !isValidProduct ||
              !quantity ||
              loadingFields.length > 0 ||
              (motivoRequerEvidencia && (!productMedia || productMedia.length === 0))
            }
          >
            Adicionar
          </Button>
        </Grid>
        <Grid item xs={6} justify="center">
          <ClearButton
            disable={loadingFields.length > 0}
            fullWidth={isMobile}
            onClick={() => {
              resetProductForm();
              clearFilters();
            }}
          />
        </Grid>
      </Grid>

      {isValidProduct === false && (
        <Typography
          variant="h5"
          component="h5"
          style={{ color: "grey", marginTop: "1rem" }}
        >
          <i
            className="fa fa-exclamation-circle"
            aria-hidden="true"
            style={{
              fontSize: "1rem",
              marginRight: "0.5rem",
            }}
          />{" "}
          Produto informado não encontrado.
        </Typography>
      )}

      {isProductAlreadySelected && (
        <Typography
          variant="h5"
          component="h5"
          style={{ color: "grey", marginTop: "1rem" }}
        >
          <i
            className="fa fa-exclamation-circle"
            aria-hidden="true"
            style={{
              fontSize: "1rem",
              marginRight: "0.5rem",
            }}
          />{" "}
          Produto já selecionado.
        </Typography>
      )}

      {openDialog && (
        <ChooseProductDialog
          open
          onClose={() => setOpenDialog(false)}
          productsList={productsList}
          setSelectedProduct={setChoosedProduct}
          totalPages={totalPages}
          setPage={setPage}
          isLoading={isLoading}
        />
      )}
    </>
  );
}
